/**
 * accountTabs — pure tab-list construction for the Account modal.
 *
 * The permission→tab matrix is a testable function rather than inline JSX
 * spreads; icons and labels live here too and the modal renders what this
 * returns.
 *
 * ORGANIZED BY TOPIC, NOT BY KIND. There used to be a "Policies" tab that
 * gathered every rule in the product and subdivided it by subject. That is the
 * opposite axis from every other tab, and the seams showed: the work-order
 * edit-lock rule ended up in a sub-tab labelled "Accounting" (because the lock
 * keys off invoice sync), while the actual accounting connection sat under
 * Integrations with the same name. Meanwhile PDR — a price book — was filed as
 * a policy despite carrying its own pdr:manage permission.
 *
 * The backend had already picked topic: the granular keys are
 * policies:accounting:manage, policies:pricing:manage, policies:workorders:manage
 * and policies:worksessions:manage — a permission per topic, not a policies
 * permission with topics beneath it. Each tab below now maps to the way
 * permissions are actually shaped.
 *
 * Ten tabs is the ceiling, and only a system admin sees all of them: the row
 * collapses to icon-only when labels will not fit and scrolls horizontally on a
 * narrow phone rather than wrapping. Real users are permission-gated to far
 * fewer, so the common case stays a short row.
 *
 * Locations is account STRUCTURE (the locations and the groups they sit in),
 * not a policy topic. Settings a location customized are listed in ONE place,
 * the "Location customizations" section (id `customizations`), across every
 * topic with a Topic filter. It replaced the per-topic "Location
 * customizations" sub-tabs of Work Orders and Time & Attendance; links to those
 * (`work-orders` / `time-attendance` with sub-tab `locations`) are redirected
 * by resolveAccountSettingsEntry.
 *
 * Visibility rules (the backend enforces each surface's own gate regardless):
 *  - overview: edit mode, everyone (the server narrows what it shows, or answers 403)
 *  - details: always
 *  - locations / people / work orders / time & attendance / financials: edit mode
 *  - work orders + time & attendance also accept policies:read as
 *    visibility-without-control — locked sub-tabs render read-only
 *  - location customizations: whoever can see work orders or time &
 *    attendance (the bulk actions gate per family inside)
 *  - feeds: data_feeds:manage (inventory vehicle feeds only)
 *  - partners: partner_connections:manage (connections, service mapping, directory)
 *  - reporting: account admins or any reports:*:account permission
 *  - pdr: pdr:manage or pdr:estimate (PDR setup is a job of its own: packs,
 *    charts, payers, R&I, rules)
 *  - system admins see everything
 */
import React from 'react';
import {
  Building2,
  MapPin,
  Users,
  ClipboardList,
  Clock,
  DollarSign,
  BarChart3,
  Database,
  Plug,
  Car,
  SlidersHorizontal,
  ListChecks,
  FileText,
  Percent,
  Landmark,
} from 'lucide-react';
import type { Tab } from '@turn-connect/ui';
import type { SettingsDeviationsArea } from '../../../services/adminApi/policyDeviations';

export type AccountModalTabId =
  | 'overview'
  | 'details'
  | 'locations'
  | 'people'
  | 'work-orders'
  // The optional Customers module's switch: a settings-page section only, never in the old tab row.
  | 'customers'
  | 'time-attendance'
  | 'financials'
  | 'invoicing'
  | 'pricing'
  | 'accounting'
  | 'bill'
  | 'pdr'
  | 'feeds'
  | 'partners'
  | 'reporting'
  | 'customizations';

/**
 * Ids that used to exist, mapped to where their content now lives.
 *
 * Deep links (`initialTab`), saved bookmarks and BDD selectors all key off the
 * id, so silently dropping the old ones would break links that are already out
 * in the world. Resolved on the way in; the old ids are never rendered.
 */
export const LEGACY_ACCOUNT_TAB_ALIASES: Record<string, AccountModalTabId> = {
  users: 'people',
  roles: 'people',
  timesheets: 'time-attendance',
  policies: 'work-orders',
  invoicing: 'financials',
  billing: 'financials',
  integrations: 'financials',
};

/** Maps any incoming tab id — current or legacy — to a tab that exists today. */
export function resolveAccountTabId(id: string | undefined | null): AccountModalTabId | undefined {
  if (!id) return undefined;
  if (id in LEGACY_ACCOUNT_TAB_ALIASES) return LEGACY_ACCOUNT_TAB_ALIASES[id];
  return ACCOUNT_TAB_LABELS[id] ? (id as AccountModalTabId) : undefined;
}

/** The topics the Location customizations section filters by (its Topic filter). */
export const CUSTOMIZATION_TOPICS: readonly SettingsDeviationsArea[] = ['work-orders', 'time-attendance'];

export const isCustomizationTopic = (value: string | undefined | null): value is SettingsDeviationsArea =>
  !!value && (CUSTOMIZATION_TOPICS as readonly string[]).includes(value);

/** Where a link into the account settings lands: the section, its sub-tab, and the customizations topic. */
export interface AccountSettingsEntry {
  tab?: AccountModalTabId;
  subTab?: string;
  /** With tab `customizations`: the Topic filter to open on (undefined = All topics). */
  topic?: SettingsDeviationsArea;
}

/**
 * Resolves an incoming tab + sub-tab (a link, a bookmark, a notification).
 * Work Orders › Location customizations and Time & Attendance › Location
 * customizations (sub-tab `locations`, also reached through the legacy tab
 * aliases) are now the one Location customizations section, opened on that
 * topic. On `customizations` itself the sub-tab names the topic.
 */
export function resolveAccountSettingsEntry(tab: string | undefined | null, subTab?: string): AccountSettingsEntry {
  const resolved = resolveAccountTabId(tab);
  if ((resolved === 'work-orders' || resolved === 'time-attendance') && subTab === 'locations') {
    return { tab: 'customizations', topic: resolved };
  }
  if (resolved === 'customizations') return { tab: resolved, topic: isCustomizationTopic(subTab) ? subTab : undefined };
  return { tab: resolved, subTab };
}

// 16px everywhere, matching the Location modal. These used to be 18px here and
// 15/16px there, so the two rows sat at visibly different weights.
const ICON = 16;

export const ACCOUNT_TAB_ICONS: Record<string, React.ReactNode> = {
  overview: <ListChecks size={ICON} />,
  details: <Building2 size={ICON} />,
  locations: <MapPin size={ICON} />,
  people: <Users size={ICON} />,
  'work-orders': <ClipboardList size={ICON} />,
  'time-attendance': <Clock size={ICON} />,
  financials: <DollarSign size={ICON} />,
  pdr: <Car size={ICON} />,
  feeds: <Database size={ICON} />,
  partners: <Plug size={ICON} />,
  reporting: <BarChart3 size={ICON} />,
  customizations: <SlidersHorizontal size={ICON} />,
};

export const ACCOUNT_TAB_LABELS: Record<string, string> = {
  overview: 'Overview',
  details: 'Profile',
  locations: 'Locations',
  people: 'People',
  'work-orders': 'Work Orders',
  'time-attendance': 'Time & Attendance',
  financials: 'Financials',
  pdr: 'PDR',
  feeds: 'Feeds',
  partners: 'Partners',
  reporting: 'Reporting',
  customizations: 'Location customizations',
};

export interface BuildAccountTabsOptions {
  isEditMode: boolean;
  isSystemAdmin: boolean;
  /** account admins or any reports:*:account permission */
  canViewAccountReports: boolean;
  permissions: string[];
}

/**
 * Which of the old modal's tabs this permission set could open, in display
 * order. The settings page now lists sections from the registry
 * (components/settings/registry); this and accountFinancialsSubTabs stay as
 * the reference its parity test compares against.
 */
export function buildAccountTabs(opts: BuildAccountTabsOptions): Tab[] {
  const { isEditMode, isSystemAdmin, canViewAccountReports, permissions } = opts;
  const has = (p: string) => permissions.includes(p);
  const tab = (id: AccountModalTabId): Tab => ({ id, label: ACCOUNT_TAB_LABELS[id], icon: ACCOUNT_TAB_ICONS[id] });

  if (!isEditMode) return [tab('details')];

  // Any granular per-area writer, the legacy coarse writers, or policies:read
  // (visibility without control — locked sub-tabs render read-only).
  const canSeePolicySurfaces =
    isSystemAdmin ||
    has('policies:read') ||
    has('policies:manage') ||
    has('policies:workorders:manage') ||
    has('policies:worksessions:manage') ||
    has('policies:accounting:manage') ||
    has('accounts:update');

  return [
    // Counts and setup to-dos, first in the list; Profile stays the section a
    // plain link opens on.
    tab('overview'),
    tab('details'),
    // Visible with account access: the list is the account's locations (the
    // server narrows it to a non-admin's own), group edits gate inside.
    tab('locations'),
    tab('people'),
    ...(canSeePolicySurfaces ? [tab('work-orders')] : []),
    ...(canSeePolicySurfaces ? [tab('time-attendance')] : []),
    // Financials always renders in edit mode: invoicing is visible to anyone
    // who can open the account, and the money surfaces that need a permission
    // hide or lock themselves per sub-tab.
    tab('financials'),
    // PDR: its own permission pair, not a policy or finance gate. Estimators
    // see charts and payers read-only; pdr:manage edits.
    ...(isSystemAdmin || has('pdr:manage') || has('pdr:estimate') ? [tab('pdr')] : []),
    // Feeds is now inventory vehicle feeds ONLY — partner integrations moved to
    // their own tab — so it gates on the feed permission, not accounting's
    // integrations:manage.
    ...(isSystemAdmin || has('data_feeds:manage') ? [tab('feeds')] : []),
    // Partners: connections, service mapping, directory. Its own concept, its
    // own permission — separate from accounting integrations and from feeds.
    ...(isSystemAdmin || has('partner_connections:manage') ? [tab('partners')] : []),
    ...(canViewAccountReports ? [tab('reporting')] : []),
    // Every location that customized a Work Orders or Time & Attendance
    // setting, in one list: shown to whoever could see either topic.
    ...(canSeePolicySurfaces ? [tab('customizations')] : []),
  ];
}

/**
 * The Financials sub-tabs this viewer sees, with their locks. A pure function
 * so the settings registry's parity test can compare against it.
 */
export function accountFinancialsSubTabs(permissions: string[], isAdmin: boolean): Tab[] {
  const has = (p: string) => permissions.includes(p);

  const canWritePricing = isAdmin || has('policies:manage') || has('policies:pricing:manage');
  // Location defaults PATCH: the account writer keys (accounts:update / account:admin).
  const canWriteDefaults = isAdmin || has('accounts:update') || has('account:admin');
  const canSeeAccounting = isAdmin || has('integrations:manage');
  const canSeeBilling = isAdmin || has('billing:report:read');

  return [
    { id: 'invoicing', label: 'Invoicing', icon: <FileText size={15} />, testId: 'financials-subtab-invoicing' },
    {
      id: 'defaults',
      label: 'Labor rate & auto-approval',
      icon: <SlidersHorizontal size={15} />,
      locked: !canWriteDefaults,
      testId: 'financials-subtab-defaults',
      lockTestId: 'financials-subtab-lock-defaults',
    },
    {
      id: 'discounts',
      label: 'Discounts & Fees',
      icon: <Percent size={15} />,
      locked: !canWritePricing,
      testId: 'financials-subtab-discounts',
      lockTestId: 'financials-subtab-lock-discounts',
    },
    // Hidden rather than locked: without integrations:manage there is nothing
    // readable behind it, unlike the policy surfaces which render read-only.
    ...(canSeeAccounting
      ? [{ id: 'accounting', label: 'Accounting', icon: <Landmark size={15} />, testId: 'financials-subtab-accounting' }]
      : []),
    ...(canSeeBilling
      ? [{ id: 'billing', label: 'Your TURN-CONNECT bill', icon: <DollarSign size={15} />, testId: 'financials-subtab-billing' }]
      : []),
  ];
}
